import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { commentsHelper, dateHelper } from "../../helpers";
import UserContext from "../../state/UserContext";
import { Badge, TextArea, Tooltip } from "../common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane as PaperPlaneReg,
  faShareNodes,
  faCircleInfo,
  faEye,
  faEyeSlash,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faPaperPlane as PaperPlaneSolid,
  faUserLock,
} from "@fortawesome/pro-solid-svg-icons";
import { NewCommentDto } from "../../types/dtos/forms";
import { FormConstants } from "../../config/forms/FormConstants";
import { AnswerSetCommentVisibility } from "../../types/collab-docs/AnswerSetCommentVisibility";

interface NewCommentFormProps {
  questionId: string;
  replyToCommentId: string | null;
  /** Whether comment visibility can be changed (if false, locked to PRIVATE) */
  allowCommentVisibilityModeChange: boolean;
  /** First name of the other participant, when known. Used in the visibility hint. */
  otherParticipantName?: string;
  onSubmit(
    newComment: NewCommentDto,
    successCallback: () => void,
    errorCallback: () => void,
  ): void;
  /** A ref for calling methods on the textarea element, e.g. to focus it */
  inputRef?: React.RefObject<HTMLTextAreaElement> | undefined;
}

export const newCommentFieldElementId = "new-comment-input";

interface VisibilityToggleButtonProps {
  label: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  disabled: boolean;
  activeBgColor: string;
  activeTextColor: string;
  activeBorderColor: string;
  isFirst: boolean;
}

function VisibilityToggleButton({
  label,
  icon,
  isSelected,
  onClick,
  disabled,
  activeBgColor,
  activeTextColor,
  activeBorderColor,
  isFirst,
}: VisibilityToggleButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={isSelected}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 transition-all focus:outline-none ${
        isSelected
          ? `${activeBgColor} ${activeTextColor} ${
              isFirst ? `border-r ${activeBorderColor}` : ""
            } font-medium`
          : `bg-transparent text-gray-400 hover:bg-gray-50 hover:text-gray-600 ${
              isFirst ? "border-r border-gray-200" : ""
            }`
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      {icon}
      <span className="text-xs">{label}</span>
    </button>
  );
}

function NewCommentForm({
  questionId,
  replyToCommentId,
  allowCommentVisibilityModeChange,
  otherParticipantName,
  onSubmit,
  inputRef = undefined,
}: NewCommentFormProps) {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);

  // State
  const [textValue, setTextValue] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [erroredWhenSaving, setErroredWhenSaving] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<AnswerSetCommentVisibility>(() =>
    commentsHelper.defaultNewCommentVisibility(
      userContext.user.client.moduleConfigs,
      allowCommentVisibilityModeChange
    )
  );

  const placeholder = (() => {
    if (allowCommentVisibilityModeChange) {
      if (replyToCommentId !== null) {
        return visibility === "PRIVATE"
          ? "Type your private reply"
          : "Type your shared reply";
      }
      return visibility === "PRIVATE"
        ? "Type your new private note"
        : "Type your new shared note";
    }
    return replyToCommentId !== null
      ? t("Pages.CollaborativeDocument.Controls.NewReplyPlaceholder")
      : t("Pages.CollaborativeDocument.Controls.NewNotePlaceholder");
  })();

  const otherPersonName = otherParticipantName?.trim();
  const visibilityHint = (() => {
    if (!allowCommentVisibilityModeChange) {
      return "";
    }
    if (visibility === "PRIVATE") {
      return t(
        "Pages.CollaborativeDocument.Controls.OnlyYouCanSeeThis",
        "Only you can see this",
      );
    }
    return otherPersonName
      ? t(
          "Pages.CollaborativeDocument.Controls.OtherPersonCanSeeComment",
          "{{name}} will be able to see this",
          { name: otherPersonName },
        )
      : t(
          "Pages.CollaborativeDocument.Controls.OtherPersonCanSeeCommentGeneric",
          "The other person will be able to see this",
        );
  })();

  // Events
  const hasCommentText = textValue.trim().length > 0;

  const handleSubmitPress = () => {
    const trimmedComment = textValue.trim();
    if (trimmedComment.length > 0) {
      // Construct the comment object
      const newComment: NewCommentDto = {
        authorId: userContext.user.id,
        comment: trimmedComment,
        questionId: questionId,
        timestamp: dateHelper.getCurrentDateUtc(),
        replyToCommentId: replyToCommentId,
        commentType: "STANDARD",
        visibility: visibility,
      };

      // Submit the comment and update the list of comments
      const successCallback = () => {
        setIsSaving(false);
        setErroredWhenSaving(false);
        // Reset the form
        setTextValue("");
      };

      const errorCallback = () => {
        setIsSaving(false);
        setErroredWhenSaving(true);
      };

      // Reset variables
      setErroredWhenSaving(false);

      // Show the saving spinner
      setIsSaving(true);

      onSubmit(newComment, successCallback, errorCallback);
    }
  };

  const handleSendClick = () => {
    if (isSaving) {
      return;
    }
    if (!hasCommentText) {
      inputRef?.current?.focus();
      return;
    }
    handleSubmitPress();
  };

  return (
    <div className="border border-gray-300 rounded-lg bg-white shadow-sm mb-3">
      <div className="px-4 pt-3 pb-2">
        <TextArea
          inputId={newCommentFieldElementId}
          placeholder={placeholder}
          value={textValue}
          onChange={setTextValue}
          className="block w-full border border-gray-200 rounded-md bg-white text-sm px-3 py-2 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition-colors"
          minRows={2}
          maxRows={4}
          inputRef={inputRef}
          showValidationErrors={false}
          maxLength={FormConstants.MaxLengths.AnswerSetComment}
        />
        <div className="mt-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center">
            {allowCommentVisibilityModeChange ? (
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm font-medium">
                  <VisibilityToggleButton
                    label="Shared"
                    icon={<FontAwesomeIcon icon={faShareNodes} size="xs" />}
                    isSelected={visibility === "SHARED"}
                    onClick={() => setVisibility("SHARED")}
                    disabled={isSaving}
                    activeBgColor="bg-blue-50"
                    activeTextColor="text-blue-700"
                    activeBorderColor="border-blue-200"
                    isFirst={true}
                  />
                  <VisibilityToggleButton
                    label="Private"
                    icon={<FontAwesomeIcon icon={faUserLock} size="xs" />}
                    isSelected={visibility === "PRIVATE"}
                    onClick={() => setVisibility("PRIVATE")}
                    disabled={isSaving}
                    activeBgColor="bg-amber-50"
                    activeTextColor="text-amber-700"
                    activeBorderColor="border-amber-200"
                    isFirst={false}
                  />
                </div>
                <Tooltip
                  triggerElement={
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none flex-shrink-0"
                      aria-label={t(
                        "Pages.CollaborativeDocument.Controls.PrivateNoteTooltip"
                      )}
                    >
                      <FontAwesomeIcon icon={faCircleInfo} size="sm" />
                    </button>
                  }
                  content=""
                  htmlContent={
                    <span className="text-left leading-relaxed whitespace-normal block">
                      {t(
                        "Pages.CollaborativeDocument.Controls.PrivateNoteTooltip",
                        {
                          PRODUCT_NAME: userContext.user.client.productName,
                        }
                      )}
                    </span>
                  }
                  appearAt="top"
                  align="center"
                />
              </div>
            ) : (
              <div>
                <Badge
                  text="Private"
                  textColourClassName="text-gray-600"
                  backgroundColourClassName="bg-gray-100"
                  marginClassName="m-0"
                />
              </div>
            )}
            </div>
            <div className="flex shrink-0 items-center">
            <button
              type="button"
              onClick={handleSendClick}
              disabled={isSaving}
              className={`note-submit-buttongroup inline-flex items-center justify-center rounded-full p-1.5 transition-colors ${
                hasCommentText
                  ? "primary-pale-bg-with-opacity accent-text"
                  : "text-gray-400"
              } ${isSaving ? "cursor-not-allowed opacity-50" : ""}`}
              aria-label={t(
                "Pages.CollaborativeDocument.Controls.NewNoteLabel",
                "Add a note"
              )}
            >
              <span className="relative inline-block">
                <FontAwesomeIcon
                  icon={PaperPlaneReg}
                  size="sm"
                  className="submit-icon-regular transition-opacity group-hover:opacity-0"
                />
                <FontAwesomeIcon
                  icon={PaperPlaneSolid}
                  size="sm"
                  className="submit-icon-solid absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
                />
              </span>
            </button>
            </div>
          </div>
          {allowCommentVisibilityModeChange && (
            <p className="mt-1 flex h-4 items-center gap-1.5 text-xs leading-4 text-gray-500">
              <FontAwesomeIcon
                icon={visibility === "PRIVATE" ? faEyeSlash : faEye}
                size="xs"
                className="shrink-0 text-gray-400"
              />
              <span className="truncate">{visibilityHint}</span>
            </p>
          )}
        </div>
      </div>
      {erroredWhenSaving && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100">
          <small className="text-sm">
            <span className="text-red-600 mr-2">
              {t("Pages.CollaborativeDocument.Common.AddCommentErrored")}
            </span>
            <button
              className="text-red-600 hover:text-red-700 underline font-medium"
              onClick={handleSubmitPress}
            >
              {t("Common.TryAgain")}
            </button>
          </small>
        </div>
      )}
    </div>
  );
}

export default NewCommentForm;
